import React, { useMemo, useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import DainTheme from '../DainTheme';
import {
    useClearMonitoringAlertSuppression,
    useGetMonitoringAlerts,
    useSuppressMonitoringAlert,
} from '../api/MonitoringApi';
import RoutePaths from '../routes/RoutePaths';
import {
    MONITORING_CONTENT_CONTAINER_SX,
    MONITORING_HEADER_CONTAINER_SX,
} from './monitoringLayout';
import AlertsFilters from './AlertsFilters';
import AlertsTable from './AlertsTable';
import SuppressAlertDialog from './SuppressAlertDialog';
import { ALERT_TYPE_FILTER_ALL, filterAlertsByType } from './alertsFilterUtils';

export default function AlertsDetail() {
    const intl = useIntl();
    const navigate = useNavigate();
    const alertsQuery = useGetMonitoringAlerts();
    const suppressMutation = useSuppressMonitoringAlert();
    const clearMutation = useClearMonitoringAlertSuppression();
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [alertTypeFilter, setAlertTypeFilter] = useState(ALERT_TYPE_FILTER_ALL);

    const alerts = alertsQuery?.data?.result?.alerts ?? [];
    const filteredAlerts = useMemo(
        () => filterAlertsByType(alerts, alertTypeFilter),
        [alerts, alertTypeFilter]
    );
    const isDialogPending = suppressMutation.isPending || clearMutation.isPending;
    const dialogError =
        suppressMutation.isError || clearMutation.isError
            ? intl.formatMessage({ id: 'monitoring.alerts.suppress.error' })
            : null;

    const handleCloseDialog = () => {
        if (!isDialogPending) {
            setSelectedAlert(null);
        }
    };

    const handleConfirmSuppress = ({ alertId, days }) => {
        suppressMutation.mutate(
            { alertId, days },
            {
                onSuccess: () => {
                    setSelectedAlert(null);
                },
            }
        );
    };

    const handleClearSuppress = ({ alertId }) => {
        clearMutation.mutate(
            { alertId },
            {
                onSuccess: () => {
                    setSelectedAlert(null);
                },
            }
        );
    };

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                    }}
                >
                    <Container maxWidth={false} disableGutters sx={MONITORING_HEADER_CONTAINER_SX}>
                        <Stack alignItems="center" spacing={0.5}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                            >
                                {intl.formatMessage({ id: 'monitoring.alerts.title' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage({ id: 'monitoring.alerts.subtitle' })}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={MONITORING_CONTENT_CONTAINER_SX}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                mb: 1,
                                flexShrink: 0,
                                width: '100%',
                            }}
                        >
                            <Button
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(RoutePaths.monitoring.root)}
                            >
                                {intl.formatMessage({ id: 'monitoring.detail.back' })}
                            </Button>
                            {alerts.length > 0 && (
                                <AlertsFilters
                                    alertTypeFilter={alertTypeFilter}
                                    onAlertTypeFilterChange={setAlertTypeFilter}
                                />
                            )}
                        </Box>

                        {alertsQuery.isLoading && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.loading' })}
                            </Typography>
                        )}
                        {alertsQuery.isError && (
                            <Typography variant="body2" color="error" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.alerts.error' })}
                            </Typography>
                        )}
                        {!alertsQuery.isLoading &&
                            !alertsQuery.isError &&
                            alerts.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    {intl.formatMessage({ id: 'monitoring.alerts.empty' })}
                                </Typography>
                            )}
                        {alerts.length > 0 &&
                            (filteredAlerts.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    {intl.formatMessage({ id: 'monitoring.alerts.filter.empty' })}
                                </Typography>
                            ) : (
                                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <AlertsTable
                                        alerts={filteredAlerts}
                                        onSuppressAlert={setSelectedAlert}
                                    />
                                </Box>
                            ))}
                    </Container>
                </Box>

                <SuppressAlertDialog
                    open={Boolean(selectedAlert)}
                    alert={selectedAlert}
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirmSuppress}
                    onClear={handleClearSuppress}
                    isPending={isDialogPending}
                    errorMessage={dialogError}
                />
            </Box>
        </ThemeProvider>
    );
}
